export interface PatternDescription {
  summary: string;
  details: string;
}

export type PatternCategory = 'Reversals' | 'Continuations' | 'Triangles & Wedges';

export interface PatternMeta extends PatternDescription {
  label: string;
  category: PatternCategory;
  // A handful of the most frequently-seen types, used to default the pattern
  // filter dropdown to a manageable subset instead of all 21 flat.
  common?: boolean;
}

export const PATTERN_META: Record<string, PatternMeta> = {
  head_shoulders: {
    label: 'Head & Shoulders',
    category: 'Reversals',
    common: true,
    summary: 'Three peaks where the middle is highest — a bearish reversal signal.',
    details: 'The left shoulder and right shoulder are roughly equal in height, flanking a taller center peak (the head). A break below the neckline (connecting the two troughs) confirms the reversal.',
  },
  inv_head_shoulders: {
    label: 'Inverse H&S',
    category: 'Reversals',
    summary: 'Three troughs where the middle is deepest — a bullish reversal signal.',
    details: 'The mirror image of Head & Shoulders. The two outer troughs (shoulders) are shallower than the center (head). A break above the neckline confirms the reversal.',
  },
  double_top: {
    label: 'Double Top',
    category: 'Reversals',
    common: true,
    summary: 'Two peaks at roughly the same level — price fails to push higher.',
    details: 'After an uptrend, price reaches a high, pulls back, then rallies to the same level and fails again. Confirms bearish reversal when price breaks below the trough between the two peaks.',
  },
  double_bottom: {
    label: 'Double Bottom',
    category: 'Reversals',
    common: true,
    summary: 'Two troughs at roughly the same level — buyers defend support twice.',
    details: 'After a downtrend, price finds a low, bounces, then falls to the same level and holds. Confirms bullish reversal when price breaks above the peak between the two troughs.',
  },
  triple_top: {
    label: 'Triple Top',
    category: 'Reversals',
    summary: 'Three failed attempts to break through resistance — strong bearish reversal.',
    details: 'A more reliable (and rarer) variant of the double top. Three roughly equal peaks indicate persistent selling pressure at that level. Break below the support neckline confirms the pattern.',
  },
  triple_bottom: {
    label: 'Triple Bottom',
    category: 'Reversals',
    summary: 'Three successful defenses of support — strong bullish reversal.',
    details: 'The bullish counterpart to the triple top. Three roughly equal troughs indicate persistent buying demand. Confirmed when price breaks above the resistance neckline.',
  },
  rounding_top: {
    label: 'Rounding Top',
    category: 'Reversals',
    summary: 'Slow, curved transition from uptrend to downtrend — gradual distribution.',
    details: 'The bearish counterpart of the rounding bottom. A long arc of price highs as buying momentum fades and sellers gradually take over. Often subtle and hard to spot in real time.',
  },
  rounding_bottom: {
    label: 'Rounding Bottom',
    category: 'Reversals',
    summary: 'Slow, curved transition from downtrend to uptrend — gradual accumulation.',
    details: 'Also called a "saucer bottom." A long, gradual rounding of the price lows as sellers exhaust themselves and buyers slowly take control. Often accompanied by declining volume during the base and expanding volume on the breakout.',
  },
  cup_handle: {
    label: 'Cup & Handle',
    category: 'Continuations',
    common: true,
    summary: 'Rounded U-shaped base followed by a small pullback — bullish continuation.',
    details: 'Forms during an uptrend. The cup is a gradual, rounded decline and recovery back to the prior high. The handle is a slight drift lower. A breakout above the cup\'s rim targets the depth of the cup as the upside move.',
  },
  inv_cup_handle: {
    label: 'Inv Cup & Handle',
    category: 'Continuations',
    summary: 'Rounded inverted U-shape followed by a small bounce — bearish continuation.',
    details: 'The bearish mirror of the cup & handle. Forms during a downtrend. An arch shape followed by a brief upward handle. Breakdown below the rim confirms continuation lower.',
  },
  bull_flag: {
    label: 'Bull Flag',
    category: 'Continuations',
    common: true,
    summary: 'Sharp rally (the pole) then a brief parallel pullback — uptrend continues.',
    details: 'A high-momentum move up (the flagpole) is followed by a short consolidation drifting slightly lower in a parallel channel. The flag\'s measured move target is the height of the pole added to the breakout point.',
  },
  bear_flag: {
    label: 'Bear Flag',
    category: 'Continuations',
    common: true,
    summary: 'Sharp drop (the pole) then a brief parallel bounce — downtrend continues.',
    details: 'The bearish counterpart. A steep drop is followed by a short consolidation drifting slightly higher in a parallel channel. Breakdown from the channel resumes the downtrend.',
  },
  bull_pennant: {
    label: 'Bull Pennant',
    category: 'Continuations',
    summary: 'Sharp rally followed by a small symmetrical triangle — uptrend resumes.',
    details: 'Similar to a bull flag but the consolidation converges into a pennant (symmetrical triangle) rather than a parallel channel. The breakout target is the height of the pole.',
  },
  bear_pennant: {
    label: 'Bear Pennant',
    category: 'Continuations',
    summary: 'Sharp drop followed by a small symmetrical triangle — downtrend resumes.',
    details: 'The bearish counterpart. A steep decline is followed by a converging pennant consolidation. Breakdown from the pennant targets the height of the pole lower.',
  },
  rectangle_bull: {
    label: 'Rectangle (Bull)',
    category: 'Continuations',
    summary: 'Price bounces horizontally between two levels — expects upside breakout.',
    details: 'A continuation pattern in an uptrend. Price consolidates in a horizontal range, testing both resistance and support multiple times. A breakout above resistance with volume confirms the continuation upward.',
  },
  rectangle_bear: {
    label: 'Rectangle (Bear)',
    category: 'Continuations',
    summary: 'Price bounces horizontally between two levels — expects downside breakdown.',
    details: 'The bearish counterpart. A horizontal consolidation in a downtrend. A breakdown below support confirms resumption of the downtrend.',
  },
  ascending_triangle: {
    label: 'Ascending Triangle',
    category: 'Triangles & Wedges',
    common: true,
    summary: 'Flat resistance ceiling with rising lows — buyers gaining momentum.',
    details: 'A bullish continuation (or reversal) pattern. A horizontal upper boundary meets a rising lower trendline, compressing price. Each bounce from support is higher than the last, signaling buyer strength. Breakout above resistance is the signal.',
  },
  descending_triangle: {
    label: 'Descending Triangle',
    category: 'Triangles & Wedges',
    common: true,
    summary: 'Flat support floor with lower highs — sellers gaining control.',
    details: 'The bearish counterpart to the ascending triangle. A horizontal lower boundary meets a descending upper trendline. Each rally fails at a lower level, signaling seller strength. Breakdown below support confirms the pattern.',
  },
  symmetrical_triangle: {
    label: 'Symmetrical Triangle',
    category: 'Triangles & Wedges',
    summary: 'Converging trendlines with lower highs and higher lows — coiling energy.',
    details: 'Price compresses into a point as buyers and sellers reach equilibrium. The eventual breakout (typically in the direction of the prior trend) can be sharp. Watch the direction of the breakout, not the pattern itself.',
  },
  rising_wedge: {
    label: 'Rising Wedge',
    category: 'Triangles & Wedges',
    summary: 'Price channels upward but narrows — bearish despite rising prices.',
    details: 'Both the upper and lower trendlines slope up, but the lower rises faster, squeezing price. Despite the higher highs and higher lows, momentum is fading. A break below the lower trendline confirms the reversal.',
  },
  falling_wedge: {
    label: 'Falling Wedge',
    category: 'Triangles & Wedges',
    summary: 'Price channels downward but narrows — bullish despite falling prices.',
    details: 'Both trendlines slope down, but the upper falls faster. Selling pressure is decelerating. A break above the upper trendline signals the end of the decline and the start of an upward move.',
  },
  broadening_formation: {
    label: 'Broadening Formation',
    category: 'Triangles & Wedges',
    summary: 'Expanding price swings with higher highs and lower lows — increasing volatility.',
    details: 'Also called a megaphone pattern. Unlike triangles, the trendlines diverge outward. It signals growing disagreement among participants and often precedes a significant reversal. Generally bearish when it appears at market tops.',
  },
};

// Back-compat: plain summary/details lookup used by components that only need copy.
export const PATTERN_INFO: Record<string, PatternDescription> = Object.fromEntries(
  Object.entries(PATTERN_META).map(([key, { summary, details }]) => [key, { summary, details }])
);

export const PATTERN_CATEGORIES: PatternCategory[] = ['Reversals', 'Continuations', 'Triangles & Wedges'];

export const PATTERNS_BY_CATEGORY: Record<PatternCategory, { value: string; label: string }[]> =
  PATTERN_CATEGORIES.reduce((acc, category) => {
    acc[category] = Object.entries(PATTERN_META)
      .filter(([, meta]) => meta.category === category)
      .map(([value, meta]) => ({ value, label: meta.label }));
    return acc;
  }, {} as Record<PatternCategory, { value: string; label: string }[]>);

// Flat value/label list in category order — replaces the old standalone PATTERN_TYPES constant.
export const PATTERN_TYPES: { value: string; label: string }[] =
  PATTERN_CATEGORIES.flatMap((category) => PATTERNS_BY_CATEGORY[category]);

export const COMMON_PATTERN_TYPES: string[] = Object.entries(PATTERN_META)
  .filter(([, meta]) => meta.common)
  .map(([value]) => value);
